import type { FetchResult } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';

import { Plan } from '@models/stripe';

import type {
  UpdateStripeCustomerMutation,
  UpdateStripeCustomerMutationVariables,
} from 'generated/stripe.generated';
import {
  useGetListStripeInvoicesQuery,
  useGetPricesQuery,
  useGetStripeCustomerQuery,
  useUpdateStripeCustomerMutation,
} from 'generated/stripe.generated';
import type { StripeCustomer, StripePrice } from 'generated/types';
import { PlanType } from 'generated/types';

export const useGetStripeCustomer = (params: {
  account_id: string | undefined;
}) => {
  const response = useGetStripeCustomerQuery({
    variables: {
      input: {
        account_id: params.account_id ?? '',
      },
    },
    skip: !params.account_id,
  });
  return response;
};

export const useGetStripeInvoiceList = (params: { account_id: string }) => {
  const response = useGetListStripeInvoicesQuery({
    variables: {
      input: {
        account_id: params.account_id,
      },
    },
    skip: !params.account_id,
  });
  return response;
};

function reducePrices(
  prices: StripePrice[],
  priceType: 'one_time' | 'recurring'
) {
  return prices.reduce<StripePrice[]>((acc, curr) => {
    if (curr.type !== priceType) {
      return acc;
    }
    const { currency_options = [] } = curr;
    for (const { currency, unit_amount } of currency_options ?? []) {
      acc.push({ ...curr, currency, unit_amount: unit_amount ?? 0 });
    }
    return acc;
  }, []);
}

export const useStripePlans = () => {
  const { data, loading } = useGetPricesQuery({
    fetchPolicy: 'no-cache',
    returnPartialData: true,
  });
  const stripePlans = data?.getPrices ?? [];

  const planTypes: PlanType[] = [PlanType.PATIENT];
  const oneTimePrices = reducePrices(stripePlans, 'one_time');
  const recurringPrices = reducePrices(stripePlans, 'recurring');

  const plans = planTypes.reduce<Record<PlanType, Plan | null>>(
    (acc, curr) => {
      const items = recurringPrices
        .filter((s) => s.type === 'recurring' && s.metadata.planType === curr)
        .map((r) => ({
          recurring: r,
          oneTime:
            oneTimePrices.find((p) => p.id === r.metadata.pairedPrice) ?? null,
        }));

      acc[curr] = new Plan(items);
      return acc;
    },
    { PATIENT: null }
  );

  return { data: plans, loading };
};

export function useUpdateStripeCustomer(): [
  (
    params: UpdateStripeCustomerMutationVariables
  ) => Promise<FetchResult<UpdateStripeCustomerMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateStripeCustomer',
  });
  const [mutate, { loading: baseLoading }] = useUpdateStripeCustomerMutation();
  const loading = baseLoading;

  async function updateStripeCustomer(
    params: UpdateStripeCustomerMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.updateStripeCustomer) {
          return;
        }
        try {
          cache.modify<{ getStripeCustomer: StripeCustomer }>({
            fields: {
              getStripeCustomer: (_existing, { DELETE }) => {
                return DELETE;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateStripeCustomer.data && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateStripeCustomer, loading];
}
